import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QuickAddRoomModal = ({ open, onClose, propertyId, onSuccess }) => {
  const [unitTypes, setUnitTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddRoomType, setShowAddRoomType] = useState(false);
  const [formData, setFormData] = useState({
    unit_type_id: '',
    name: '',
    floor: 0
  });
  const [roomTypeForm, setRoomTypeForm] = useState({
    name: '',
    capacity: 2,
    bed_type: ''
  });

  useEffect(() => {
    if (open && propertyId) {
      fetchUnitTypes();
    }
  }, [open, propertyId]);

  const fetchUnitTypes = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/properties/${propertyId}/unit-types`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnitTypes(response.data);
      if (response.data.length > 0) {
        setFormData(prev => ({ ...prev, unit_type_id: response.data[0].id }));
      }
    } catch (error) {
      console.error('Failed to fetch room types:', error);
    }
  };

  const handleCreateRoomType = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        `${API}/properties/${propertyId}/unit-types`,
        roomTypeForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Room type created successfully');
      setRoomTypeForm({ name: '', capacity: 2, bed_type: '' });
      setShowAddRoomType(false);
      // Refresh room types
      await fetchUnitTypes();
    } catch (error) {
      console.error('Create room type error:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Failed to create room type');
    } finally {
      setLoading(false);
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        `${API}/properties/${propertyId}/units`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Room created successfully');
      setFormData({ unit_type_id: unitTypes[0]?.id || '', name: '', floor: 0 });
      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      console.error('Create room error:', error.response?.data);
      const errorMessage = error.response?.data?.detail || 'Failed to create room';
      // Show user-friendly error message
      if (errorMessage.includes('already exists')) {
        toast.error(`Error: Room ${formData.name} already exists`);
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add Room</DialogTitle>
        </DialogHeader>
        
        {unitTypes.length === 0 && !showAddRoomType ? (
          <div className="py-6 space-y-4">
            <p className="text-slate-600 text-center">No room types available. Create one to continue.</p>
            <div className="flex gap-2">
              <Button onClick={() => setShowAddRoomType(true)} className="flex-1">
                <Plus size={16} className="mr-2" />
                Create Room Type
              </Button>
              <Button onClick={onClose} variant="outline">Cancel</Button>
            </div>
          </div>
        ) : showAddRoomType ? (
          <form onSubmit={handleCreateRoomType} className="space-y-4">
            <div className="bg-cyan-50 p-3 rounded-lg mb-4">
              <p className="text-sm text-cyan-900 font-medium">Step 1: Create Room Type</p>
            </div>
            
            <div>
              <Label>Room Type Name *</Label>
              <Input
                value={roomTypeForm.name}
                onChange={(e) => setRoomTypeForm({...roomTypeForm, name: e.target.value})}
                placeholder="Standard Room"
                required
              />
            </div>

            <div>
              <Label>Capacity (guests) *</Label>
              <Input
                type="number"
                min="1"
                value={roomTypeForm.capacity}
                onChange={(e) => setRoomTypeForm({...roomTypeForm, capacity: e.target.value === '' ? '' : parseInt(e.target.value)})}
                required
              />
            </div>

            <div>
              <Label>Bed Type (optional)</Label>
              <Input
                value={roomTypeForm.bed_type}
                onChange={(e) => setRoomTypeForm({...roomTypeForm, bed_type: e.target.value})}
                placeholder="King, Queen, etc."
              />
            </div>

            <div className="flex gap-2">
              <Button type="submit" className="flex-1" disabled={loading}>
                {loading ? 'Creating...' : 'Create Room Type'}
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowAddRoomType(false)}>
                Back
              </Button>
            </div>
          </form>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Room Type *</Label>
              <div className="flex gap-2">
                <select
                  value={formData.unit_type_id}
                  onChange={(e) => setFormData({...formData, unit_type_id: e.target.value})}
                  className="flex-1 px-3 py-2 border border-slate-300 rounded-md"
                  required
                >
                  {unitTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </select>
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm"
                  onClick={() => setShowAddRoomType(true)}
                >
                  <Plus size={14} />
                </Button>
              </div>
            </div>

            <div>
              <Label>Room Number/Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="101"
                required
              />
            </div>

            <div>
              <Label>Floor</Label>
              <Input
                type="number"
                min="0"
                value={formData.floor}
                onChange={(e) => setFormData({...formData, floor: e.target.value === '' ? '' : parseInt(e.target.value)})}
              />
            </div>

            <div className="flex gap-2">
              <Button type="submit" className="flex-1" disabled={loading}>
                <Plus size={16} className="mr-2" />
                {loading ? 'Creating...' : 'Create Room'}
              </Button>
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default QuickAddRoomModal;
